import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllStaff,
  getStaffById,
  createStaff,
  updateStaff,
  deleteStaff
} from '../services/staffService';
import Notification from '../components/Common/Notification';

export const StaffContext = createContext();

export const StaffProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [staffs, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllStaff = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllStaff(authHeaders);
      setStaff(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching staffs.';
      showNotification(errorMessage, true);
    }
  };

  const fetchStaffById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getStaffById(id, authHeaders);
      setSelectedStaff(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching staff with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addStaff = async (staffData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newStaff = await createStaff(staffData, authHeaders);
      setStaff(prevStaff => [...prevStaff, newStaff]);
      showNotification('Staff added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding staff.';
      showNotification(errorMessage, true);
    }
  };

  const editStaff = async (id, staffData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedStaff = await updateStaff(id, staffData, authHeaders);
      setStaff(prevStaff =>
        prevStaff.map(staff => (staff.id === id ? updatedStaff : staff))
      );
      showNotification('Staff updated successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating staff.';
      showNotification(errorMessage, true);
    }
  };

  const removeStaff = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteStaff(id, authHeaders);
      setStaff(prevStaff => prevStaff.filter(staff => staff.id !== id));
      showNotification('Staff removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing staff.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllStaff();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <StaffContext.Provider
      value={{
        staffs,
        selectedStaff,
        fetchAllStaff,
        fetchStaffById,
        addStaff,
        editStaff,
        removeStaff,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </StaffContext.Provider>
  );
};
